import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Store, BookmarkCheck, CreditCard, UserCircle, LogOut, Menu, Coins, BarChart3, FileStack,
  Upload, Building2, Wallet, CalendarClock, Package, Settings, ScrollText, Radar,
} from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { ThemeToggle } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";

const AGENCY_NAV = [
  { to: "/app", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/app/marketplace", label: "Marketplace", icon: Store },
  { to: "/app/my-leads", label: "My Leads", icon: BookmarkCheck },
  { to: "/app/billing", label: "Billing", icon: CreditCard },
  { to: "/app/profile", label: "Profile", icon: UserCircle },
];

const ADMIN_NAV = [
  { to: "/admin", label: "Analytics", icon: BarChart3, end: true },
  { to: "/admin/leads", label: "Leads", icon: FileStack },
  { to: "/admin/import", label: "CSV Import", icon: Upload },
  { to: "/admin/agencies", label: "Agencies", icon: Building2 },
  { to: "/admin/payments", label: "Payments", icon: Wallet },
  { to: "/admin/subscriptions", label: "Subscriptions", icon: CalendarClock },
  { to: "/admin/plans", label: "Plans & Credits", icon: Package },
  { to: "/admin/settings", label: "Payment Settings", icon: Settings },
  { to: "/admin/audit", label: "Audit Logs", icon: ScrollText },
  { to: "/admin/profile", label: "Profile", icon: UserCircle },
];

const Brand = () => (
  <div className="flex items-center gap-2.5 px-5 h-16 border-b">
    <div className="h-8 w-8 rounded-lg bg-primary text-primary-foreground flex items-center justify-center"><Radar className="h-4 w-4" /></div>
    <span className="font-display font-extrabold tracking-tight text-lg">LeadVault</span>
  </div>
);

const NavList = ({ items, onNavigate }) => (
  <nav className="flex-1 overflow-y-auto p-3 space-y-0.5">
    {items.map(({ to, label, icon: Icon, end }) => (
      <NavLink key={to} to={to} end={end} onClick={onNavigate} data-testid={`nav-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
        className={({ isActive }) => cn("flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors",
          isActive ? "bg-accent text-accent-foreground" : "text-muted-foreground hover:text-foreground hover:bg-secondary")}>
        <Icon className="h-4 w-4" />{label}
      </NavLink>
    ))}
  </nav>
);

const SidebarBody = ({ items, onNavigate, role }) => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  return (
    <div className="flex flex-col h-full">
      <Brand />
      <p className="px-6 pt-5 pb-1 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{role === "super_admin" ? "Super Admin" : "Agency Workspace"}</p>
      <NavList items={items} onNavigate={onNavigate} />
      <div className="p-3 border-t">
        <button data-testid="logout-button" onClick={() => { logout(); navigate("/login"); }}
          className="w-full flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors">
          <LogOut className="h-4 w-4" />Log out
        </button>
      </div>
    </div>
  );
};

const CreditBadge = ({ balance }) => (
  <NavLink to="/app/billing" data-testid="agency-credit-balance"
    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-amber-500/10 text-amber-700 dark:text-amber-400 border border-amber-500/20 hover:bg-amber-500/20 transition-colors">
    <Coins className="h-3.5 w-3.5" /><span className="font-mono">{balance ?? 0}</span> credits
  </NavLink>
);

export default function AppShell() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const items = user.role === "super_admin" ? ADMIN_NAV : AGENCY_NAV;
  return (
    <div className="min-h-screen flex bg-background">
      <aside className="hidden lg:flex w-64 shrink-0 border-r bg-card fixed inset-y-0 left-0 z-40"><SidebarBody items={items} role={user.role} /></aside>
      <div className="flex-1 lg:pl-64 min-w-0">
        <header className="sticky top-0 z-30 h-16 border-b bg-background/80 backdrop-blur-md flex items-center gap-3 px-4 sm:px-6 lg:px-8">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="lg:hidden" data-testid="mobile-menu-button"><Menu className="h-5 w-5" /></Button>
            </SheetTrigger>
            <SheetContent side="left" className="p-0 w-72"><SidebarBody items={items} role={user.role} onNavigate={() => setOpen(false)} /></SheetContent>
          </Sheet>
          <div className="min-w-0">
            <p className="text-sm font-semibold truncate" data-testid="header-org-name">{user.role === "agency" ? user.agency?.name : "Platform Control"}</p>
            <p className="text-xs text-muted-foreground truncate">{user.email}</p>
          </div>
          <div className="ml-auto flex items-center gap-2">
            {user.role === "agency" && <CreditBadge balance={user.credit_balance} />}
            <ThemeToggle />
          </div>
        </header>
        <main className="p-4 sm:p-6 lg:p-8 max-w-[1400px]"><Outlet /></main>
      </div>
    </div>
  );
}
